/**
 * シフト提出(2026-09-12 新設)。本人が「その月のどの日に何時から何時まで入れるか」を出す。
 *  - operator: 自分の分。スマートフォン前提
 *  - admin: 対象者を選んで代理入力・確認ができる
 * ここで出した時間が「提出シフト時間」。管理者が出勤表で直した結果が「修正シフト時間」になる。
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import { useUnsavedGuard } from '@core/components/UnsavedGuard';
import { todayStr, weekdayLabel } from '@core/lib/date';
import { dayColorOf } from '@core/lib/holidays';
import { WORK_STYLE_LABELS, type DateStr, type MonthStr, type Operator, type WorkStyle } from '@core/lib/types';
import { fetchOperatorById, fetchOperators } from '@core/masters/operatorRepo';
import { addMonths, daysInMonth, fmtNum } from '@core/dashboard/calc';
import { fetchSubmission, saveSubmission, submittedHours, type SubmitDay } from './shiftSubmitRepo';

/** 8:00〜19:00 の30分刻み(2026-09-12 指示) */
const hours = Array.from({ length: 23 }, (_, i) => 8 + i * 0.5);
const hhmm = (h: number) => `${Math.floor(h)}:${h % 1 ? '30' : '00'}`;

export default function ShiftSubmitTab() {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const [month, setMonth] = useState<MonthStr>(todayStr().slice(0, 7) as MonthStr);
  const [opId, setOpId] = useState(user?.op_id ?? '');
  const [operators, setOperators] = useState<Operator[]>([]);
  const [days, setDays] = useState<Record<string, SubmitDay>>({});
  const [note, setNote] = useState('');
  const [bbId, setBbId] = useState('');
  const [me, setMe] = useState<Operator | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const savedRef = useRef('');
  const guard = useUnsavedGuard(dirty);

  useEffect(() => {
    if (isAdmin) {
      void fetchOperators()
        .then(setOperators)
        .catch(() => undefined);
    } else if (user?.op_id) {
      // operator は一覧を読めないので自分のドキュメントだけ読む
      void fetchOperatorById(user.op_id)
        .then(setMe)
        .catch(() => undefined);
    }
  }, [isAdmin, user?.op_id]);

  const load = useCallback(async () => {
    if (!opId) {
      setDays({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const s = await fetchSubmission(month, opId);
      const next = s?.days ?? {};
      setDays(next);
      setNote(s?.note ?? '');
      setBbId(s?.bb_id ?? '');
      savedRef.current = JSON.stringify(next);
      setDirty(false);
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [month, opId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = (day: string, v: SubmitDay | null) => {
    setDays((prev) => {
      const next = { ...prev };
      if (v === null) delete next[day];
      else next[day] = v;
      setDirty(JSON.stringify(next) !== savedRef.current);
      return next;
    });
  };

  const total = useMemo(() => submittedHours(days), [days]);
  const target = useMemo(() => (isAdmin ? (operators.find((o) => o.id === opId) ?? null) : me), [isAdmin, operators, opId, me]);
  const name = target?.name ?? user?.name ?? '';
  /**
   * 列は **出勤形態ごと**(2026-09-12 指示)。同じ出勤形態のBB IDが複数あっても1列にまとめる。
   * 品川と在宅の2つを持つ人だけ2列になる。報酬と BB ID は出さない
   */
  const styleCols = useMemo(() => {
    const seen = new Map<WorkStyle, string>();
    for (const a of target?.bb_accounts ?? []) if (!seen.has(a.work_style)) seen.set(a.work_style, a.bb_id);
    return [...seen.entries()].map(([ws, bb]) => ({ ws, bb }));
  }, [target]);
  /** BB ID → 出勤形態(その日の入力がどの列のものかを見分ける) */
  const wsOfBb = useMemo(() => new Map((target?.bb_accounts ?? []).map((a) => [a.bb_id, a.work_style])), [target]);
  const accounts = target?.bb_accounts ?? [];
  // 出勤形態×報酬区分は列で分ける。保存する代表値は1つ目(集計の互換用)
  useEffect(() => {
    if (!bbId && accounts.length >= 1) setBbId(accounts[0]?.bb_id ?? '');
  }, [bbId, accounts]);

  const save = async () => {
    if (!user || !opId) return;
    setSaving(true);
    try {
      await saveSubmission({ month, opId, name, bbId, days, note, userId: user.id });
      savedRef.current = JSON.stringify(days);
      setDirty(false);
      toast('シフトを提出しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '提出に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const list = Array.from({ length: daysInMonth(month) }, (_, i) => String(i + 1).padStart(2, '0'));

  return (
    <>
      {guard.modal}
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          シフト提出
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <button className="txn-link" onClick={() => setMonth(addMonths(month, -1) as MonthStr)}>前の月</button>
            <b>{month.slice(0, 4)}年{Number(month.slice(5))}月</b>
            <button className="txn-link" onClick={() => setMonth(addMonths(month, 1) as MonthStr)}>次の月</button>
          </span>
          {isAdmin && (
            <select value={opId} onChange={(e) => setOpId(e.target.value)} style={{ minWidth: 200, height: 30 }}>
              <option value="">スタッフを選ぶ</option>
              {operators.filter((o) => o.status === 'active').map((o) => (
                <option key={o.id} value={o.id}>{o.name}({o.op_code})</option>
              ))}
            </select>
          )}
          <span className="hint" style={{ fontWeight: 400 }}>提出シフト時間 {fmtNum(total, 1)}h</span>
          <span style={{ marginLeft: 'auto' }}>
            <button className="btn" disabled={!opId || !dirty || saving} onClick={() => void save()}>
              {saving ? '提出中…' : '提出する'}
            </button>
          </span>
        </h3>

        {!opId ? (
          <div className="empty">{isAdmin ? 'スタッフを選んでください' : 'アカウントにアポインターが紐づいていません。管理者に連絡してください。'}</div>
        ) : loading ? (
          <div className="empty">読み込み中…</div>
        ) : (
          <>
            <div className="table-scroll">
              <table className="fit-table">
                <thead>
                  <tr>
                    <th rowSpan={2} style={{ width: 92 }}>日付</th>
                    {styleCols.map((sc, i) => (
                      <th key={sc.ws} colSpan={2} className={`ws-col ws-${i % 2}`} style={{ minWidth: 190 }}>
                        {WORK_STYLE_LABELS[sc.ws]}
                      </th>
                    ))}
                    <th rowSpan={2} className="num" style={{ width: 64 }}>時間</th>
                  </tr>
                  <tr>
                    {styleCols.map((sc, i) => (
                      <Fragment key={sc.ws}>
                        <th className={`ws-col ws-${i % 2}`} style={{ width: 95 }}>開始</th>
                        <th className={`ws-col ws-${i % 2}`} style={{ width: 95 }}>終了</th>
                      </Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {list.map((d) => {
                    const date = `${month}-${d}` as DateStr;
                    const v = days[d];
                    const dow = weekdayLabel(date);
                    const c = dayColorOf(date);
                    const cls = c === 'red' ? 'sun' : c === 'blue' ? 'sat' : '';
                    return (
                      <tr key={d}>
                        <td className={cls}>{Number(d)}日{dow}</td>
                        {styleCols.map((sc, i) => {
                          // その日に入っているのはこの出勤形態か(1日1つだけ)
                          const curWs = v ? (wsOfBb.get(v.bb_id ?? '') ?? styleCols[0]?.ws) : undefined;
                          const mine = v && curWs === sc.ws ? v : undefined;
                          return (
                            <Fragment key={sc.ws}>
                              <td className={`ws-col ws-${i % 2}`}>
                                <select
                                  value={mine?.start ?? ''}
                                  onChange={(e) =>
                                    patch(
                                      d,
                                      e.target.value === ''
                                        ? null
                                        : { bb_id: sc.bb, start: Number(e.target.value), end: mine?.end ?? Math.min(19, Number(e.target.value) + 1) },
                                    )
                                  }
                                  style={{ width: '100%', height: 30 }}
                                >
                                  <option value="">休み</option>
                                  {hours.map((h) => <option key={h} value={h}>{hhmm(h)}</option>)}
                                </select>
                              </td>
                              <td className={`ws-col ws-${i % 2}`}>
                                <select
                                  value={mine?.end ?? ''}
                                  disabled={!mine}
                                  onChange={(e) => mine && patch(d, { bb_id: sc.bb, start: mine.start, end: Number(e.target.value) })}
                                  style={{ width: '100%', height: 30 }}
                                >
                                  <option value="">—</option>
                                  {hours.map((h) => <option key={h} value={h}>{hhmm(h)}</option>)}
                                </select>
                              </td>
                            </Fragment>
                          );
                        })}
                        <td className="num">{v ? fmtNum(Math.max(0, v.end - v.start), 1) : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 10 }}>
              <label className="hint" style={{ display: 'block', marginBottom: 4 }}>連絡事項(任意)</label>
              <textarea
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  setDirty(true);
                }}
                rows={2}
                style={{ width: '100%', maxWidth: 560, fontFamily: 'inherit', fontSize: 13, padding: 6, border: '1px solid var(--line)', borderRadius: 6 }}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
